import { useState } from 'react';
import { useSnackbar } from 'notistack';

import { useTranslate } from 'src/locales';
import { generateUpdateProfileError } from 'src/services/applications/applications.utils';
import { WriteBackConflict, WriteBackFieldKey } from 'src/services/candidates/candidates.types';
import { useUpdateApplicationContactMutation } from 'src/services/applications/applications.service';
import { ApplicationContactUpdate, ApplicationContactResponse } from 'src/services/applications/applications.types';

import WriteBackConfirmDialog from 'src/sections/profile/write-back/write-back-confirm-dialog';

type SaveArgs = { applicationId: string; orgCandidateId?: string; data: ApplicationContactUpdate };

/**
 * Saves an application's contact details. When the candidate record already holds a different email
 * or phone, the recruiter is asked whether to update the record too, with the same confirm dialog as
 * the JT1-1534 profile write-back. The first save always stores the application's own values;
 * confirming re-sends it with `apply_write_back`. Render the returned `dialog`.
 */
export function useApplicationContactSave() {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const [updateContact, { isLoading: saving }] = useUpdateApplicationContactMutation();

  const [conflicts, setConflicts] = useState<WriteBackConflict[]>([]);
  const [pending, setPending] = useState<SaveArgs | null>(null);
  const [confirming, setConfirming] = useState(false);

  const close = () => {
    setConflicts([]);
    setPending(null);
    setConfirming(false);
  };

  const save = async (args: SaveArgs): Promise<ApplicationContactResponse> => {
    const response = await updateContact(args).unwrap();
    const responseConflicts = response?.write_back?.conflicts ?? [];
    if (responseConflicts.length > 0) {
      setPending(args);
      setConflicts(responseConflicts);
    }
    return response;
  };

  const onConfirm = async (fields: WriteBackFieldKey[]) => {
    if (!pending) return;
    setConfirming(true);
    try {
      await updateContact({ ...pending, data: { ...pending.data, apply_write_back: fields } }).unwrap();
      enqueueSnackbar(t('profile.write_back.success'), { variant: 'success' });
      close();
    } catch (error) {
      enqueueSnackbar(generateUpdateProfileError(error), { variant: 'error' });
      setConfirming(false);
    }
  };

  const dialog = (
    <WriteBackConfirmDialog
      open={pending !== null && conflicts.length > 0}
      conflicts={conflicts}
      loading={confirming}
      onConfirm={onConfirm}
      onCancel={close}
    />
  );

  return { save, saving, dialog };
}
